/**
 * Turning the ends of a route into place names.
 *
 * "Calais" and "the layby on the B6105" are how people talk about a drive;
 * a pair of coordinates is not. Done once, when a route is saved, so the name
 * travels with the route and every phone that opens it gets the same answer
 * without spending a lookup of its own.
 *
 * Failure is entirely acceptable here. A route with no place names is a route
 * that shows its own name and nothing else, which is what it did before — so
 * every path through this returns null rather than throwing.
 */

import { haversineMeters } from './geo.js';
import type { LngLat } from './types.js';

export interface GeocodeOptions {
  accessToken: string;
  fetchImpl?: typeof fetch;
  baseUrl?: string;
  signal?: AbortSignal;
}

/** Somewhere you can point at: what to call it, where it is. */
export interface Place {
  /** Short, for a list row or a stop: "Foulsham". */
  name: string;
  /** The fuller line under it: "Foulsham, Norfolk, England". Never the country tail alone. */
  address: string;
  point: LngLat;
}

const DEFAULT_BASE = 'https://api.mapbox.com/geocoding/v5/mapbox.places';

/**
 * Searching by name is a different Mapbox product from looking up a
 * coordinate, and has to be.
 *
 * The geocoder accepts `types=poi` and then returns almost no businesses:
 * "Aldi Norwich" came back as Aldis Road, Acle. Search Box is the one built
 * for places people name out loud — shops, pubs, "Snake Pass Summit" — and
 * it still answers villages, postcodes and streets as well as the geocoder
 * did.
 *
 * The plain `forward` endpoint rather than the interactive suggest/retrieve
 * pair: one request instead of two, no session token to carry about, and it
 * bills against the 25,000-a-month request allowance rather than the 2,500
 * sessions.
 */
const DEFAULT_SEARCH_BASE = 'https://api.mapbox.com/search/searchbox/v1/forward';

/**
 * The types worth offering, largest last.
 *
 * A driver naming a stop means a village, a pub or a postcode, not a country.
 * Leaving `country` and `region` out of the list keeps "Norfolk" from being
 * the top answer when someone is reaching for a village in it.
 */
const PLACE_TYPES = 'address,poi,postcode,neighborhood,locality,place';

/**
 * What Search Box may offer. A driver naming a stop means a shop, a village
 * or a postcode — never a country, and never a whole region.
 */
const SEARCH_TYPES = 'poi,address,street,postcode,neighborhood,locality,place';

/** One Search Box feature as somewhere you can drive to. */
function searchResultFrom(feature: Record<string, any>): Place | null {
  const coordinates = feature?.geometry?.coordinates;
  if (!Array.isArray(coordinates) || coordinates.length < 2) return null;
  const [lng, lat] = coordinates;
  if (typeof lng !== 'number' || typeof lat !== 'number') return null;

  const properties = feature.properties ?? {};
  const name = typeof properties.name === 'string' ? properties.name.trim() : '';
  if (!name) return null;

  /*
    `full_address` says where a shop is; `place_formatted` is what is left
    when there is no street to give — a village's county and country. Either
    way the country is trimmed off the end, as it is for the geocoder.
  */
  const full =
    (typeof properties.full_address === 'string' && properties.full_address) ||
    (typeof properties.place_formatted === 'string' && properties.place_formatted) ||
    '';

  return { name, address: shortAddress(full, name), point: [lng, lat] };
}

/**
 * Trims the country off the end of Mapbox's full postal name.
 *
 * Every result ends ", United Kingdom", which is the one part nobody needs on
 * a screen this size and the part that pushes the useful end out of view.
 */
function shortAddress(placeName: string, name: string): string {
  const parts = placeName.split(',').map((part) => part.trim()).filter(Boolean);
  if (parts[0] === name) parts.shift();
  if (parts.length > 1) parts.pop();
  return parts.join(', ');
}

function placeFrom(feature: Record<string, any>): Place | null {
  const centre = feature?.center;
  if (!Array.isArray(centre) || centre.length < 2) return null;
  const [lng, lat] = centre;
  if (typeof lng !== 'number' || typeof lat !== 'number') return null;

  const name = typeof feature.text === 'string' && feature.text.trim() ? feature.text.trim() : null;
  const full = typeof feature.place_name === 'string' ? feature.place_name.trim() : '';
  if (!name && !full) return null;

  return {
    name: name ?? full.split(',')[0]!.trim(),
    address: shortAddress(full, name ?? ''),
    point: [lng, lat],
  };
}

export interface SearchOptions extends GeocodeOptions {
  /** Bias towards here — the car, usually. Nearby answers come first. */
  near?: LngLat;
  limit?: number;
  /**
   * Which countries to search, as ISO codes. Defaults to the UK and the
   * near continent: this is a UK car group that drives to the Alps, and an
   * unrestricted search puts Boston, Massachusetts above Boston, Lincolnshire.
   */
  countries?: string[];
}

const DEFAULT_COUNTRIES = ['gb', 'ie', 'fr', 'be', 'nl', 'lu', 'de', 'ch', 'at', 'it', 'es', 'pt'];

/**
 * Places matching what someone has typed so far.
 *
 * Built for a keystroke: `autocomplete` on, a short limit, and every failure —
 * network, quota, a half-typed query Mapbox dislikes — coming back as an empty
 * list. A search box that throws while you type is worse than one that waits.
 */
export async function searchPlaces(query: string, options: SearchOptions): Promise<Place[]> {
  const text = query.trim();
  if (!text) return [];

  const limit = options.limit ?? 6;
  const ask = (types: string, count: number) =>
    requestSearch(text, types, count, options);

  /*
    Everything, and alongside it towns alone.

    Asked for "Adenau" from Norfolk, Search Box fills its answer with things
    named after the town — apartments, a chapel, a car park — and leaves the
    town itself out: it only ranks first from somewhere nearby. Planning a run
    to the Eifel from home, that is exactly the search that matters, and it
    got worse when changing a stop started searching around that stop rather
    than the last one. Asked for towns alone it finds Adenau, Nürburg, Spa and
    Bastogne from anywhere, so a town whose name is what was typed goes first.
    Everything else keeps its own order after it.
  */
  const [everything, towns] = await Promise.all([ask(SEARCH_TYPES, limit), ask(TOWN_TYPES, 3)]);

  const wanted = normaliseName(text);
  const matching = towns.filter((town) => {
    const name = normaliseName(town.name);
    // "Adenau", or "Adenau Germany": the town is what was typed, or its start.
    return name === wanted || name.startsWith(wanted) || wanted.startsWith(`${name} `);
  });

  const merged = [...matching];
  for (const place of everything) {
    const same = merged.some(
      (kept) =>
        normaliseName(kept.name) === normaliseName(place.name) &&
        haversineMeters(kept.point, place.point) < SAME_PLACE_METERS,
    );
    if (!same) merged.push(place);
  }
  return merged.slice(0, limit);
}

/** Towns and villages, for the search alongside the main one. */
const TOWN_TYPES = 'place,locality';

/** Two results this close with the same name are the same place, found twice. */
const SAME_PLACE_METERS = 2_000;

/** Lower case, accents and punctuation gone: "Nürburg," reads as "nurburg". */
function normaliseName(name: string): string {
  return name
    .normalize('NFD')
    .replace(/[\u0300-\u036f]/g, '')
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, ' ')
    .trim();
}

/** One Search Box request; nothing rather than an error, so one failing never sinks the other. */
async function requestSearch(
  text: string,
  types: string,
  limit: number,
  options: SearchOptions,
): Promise<Place[]> {
  const fetcher = options.fetchImpl ?? globalThis.fetch.bind(globalThis);
  const base = options.baseUrl ?? DEFAULT_SEARCH_BASE;

  const params = new URLSearchParams({
    q: text,
    access_token: options.accessToken,
    limit: String(limit),
    types,
    country: (options.countries ?? DEFAULT_COUNTRIES).join(',').toUpperCase(),
  });
  if (options.near) {
    params.set('proximity', `${options.near[0].toFixed(5)},${options.near[1].toFixed(5)}`);
  }

  try {
    const response = await fetcher(`${base}?${params.toString()}`, options.signal ? { signal: options.signal } : {});
    if (!response.ok) return [];
    const json = (await response.json()) as { features?: Array<Record<string, any>> };
    return (json.features ?? [])
      .map(searchResultFrom)
      .filter((place): place is Place => place !== null);
  } catch {
    return [];
  }
}

/**
 * What is at this point — for the map picker, where someone has dragged the
 * map under a pin and needs to see what they have chosen before confirming.
 */
export async function placeAt(point: LngLat, options: GeocodeOptions): Promise<Place | null> {
  const fetcher = options.fetchImpl ?? globalThis.fetch.bind(globalThis);
  const base = options.baseUrl ?? DEFAULT_BASE;

  const params = new URLSearchParams({
    access_token: options.accessToken,
    limit: '1',
    types: PLACE_TYPES,
  });

  try {
    const response = await fetcher(
      `${base}/${point[0].toFixed(6)},${point[1].toFixed(6)}.json?${params.toString()}`,
      options.signal ? { signal: options.signal } : {},
    );
    if (!response.ok) return null;
    const json = (await response.json()) as { features?: Array<Record<string, any>> };
    const place = json.features?.[0] ? placeFrom(json.features[0]!) : null;
    /*
      The pin is where the driver put it, not where the nearest thing with a
      name happens to be. Keeping the geocoder's coordinate would quietly move
      a stop from the layby to the village half a mile away.
    */
    return place ? { ...place, point } : null;
  } catch {
    return null;
  }
}

/**
 * The short name of the nearest thing worth naming.
 *
 * Types are ordered deliberately: an address where there is one, because that
 * is what a start point in a town usually is, then progressively larger areas
 * for a start point that is a gateway on a moor. `text` rather than
 * `place_name` — the latter is the full postal form down to the country, and
 * a route list has no room for "Glossop, Derbyshire, England, United Kingdom".
 */
export async function placeNameFor(
  point: LngLat,
  { accessToken, fetchImpl, baseUrl, signal }: GeocodeOptions,
): Promise<string | null> {
  const fetcher = fetchImpl ?? globalThis.fetch.bind(globalThis);
  const base = baseUrl ?? 'https://api.mapbox.com/geocoding/v5/mapbox.places';

  const params = new URLSearchParams({
    access_token: accessToken,
    limit: '1',
    types: 'address,poi,neighborhood,locality,place',
  });

  try {
    const response = await fetcher(
      `${base}/${point[0].toFixed(6)},${point[1].toFixed(6)}.json?${params.toString()}`,
      signal ? { signal } : {},
    );
    if (!response.ok) return null;

    const json = (await response.json()) as { features?: Array<{ text?: unknown }> };
    const text = json.features?.[0]?.text;
    return typeof text === 'string' && text.trim().length > 0 ? text.trim() : null;
  } catch {
    return null;
  }
}

/**
 * Names for both ends, preferring whatever the planner already called them.
 *
 * A waypoint the user has named is better than anything a geocoder will say —
 * they called it "Mick's" for a reason — so those are left alone and only the
 * unnamed ends cost a request.
 */
export async function nameRouteEnds(
  start: { lng: number; lat: number; name?: string },
  end: { lng: number; lat: number; name?: string },
  options: GeocodeOptions,
): Promise<{ startName: string | null; endName: string | null }> {
  const [startName, endName] = await Promise.all([
    start.name?.trim() ? Promise.resolve(start.name.trim()) : placeNameFor([start.lng, start.lat], options),
    end.name?.trim() ? Promise.resolve(end.name.trim()) : placeNameFor([end.lng, end.lat], options),
  ]);
  return { startName, endName };
}

/**
 * Whether a stop is named by nothing but a UK postcode.
 *
 * A postcode is an area — a street's worth of houses, sometimes a village's —
 * and geocoded it lands on the middle of that area, not on anyone's door.
 * Jimbo's drive to IP24 2NJ ended 307 m from where he parked, and the app
 * spent the last of it telling him to turn round towards a point in a field.
 * The planner uses this to say so, and to offer placing the pin by hand.
 */
export function isPostcodeOnly(name: string | null | undefined): boolean {
  if (!name) return false;
  return /^[A-Z]{1,2}\d[A-Z\d]?\s*\d[A-Z]{2}$/i.test(name.trim());
}
